import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';

export interface DealTask {
  id: string;
  deal_id: string;
  title: string;
  description: string;
  status: string;
  assigned_to: string;
  start_date?: string;
  due_date: string;
  completion_date: string;
  progress_percentage: number;
  depends_on_task_id: string;
  payment_amount?: number;
  payment_status?: 'unpaid' | 'due' | 'paid';
  paid_at?: string;
  paid_by_profile_id?: string;
  payment_transaction_id?: string;
  created_at: string;
  updated_at: string;
}

export interface BrandDeal {
  id: string;
  title: string;
  description: string;
  deal_value: number;
  deal_type: string;
  status: string;
  priority: string;
  estimated_completion_date: string;
  actual_completion_date: string;
  talent_profile_id: string;
  brand_profile_id: string;
  campaign_id: string;
  created_at: string;
  updated_at: string;
  talent?: {
    name: string;
    sport: string;
    photo?: string;
    total_followers?: number;
  };
  campaign?: {
    name: string;
    status: string;
  };
  tasks?: DealTask[];
  progress?: any[];
}

export interface Campaign {
  id: string;
  name: string;
  description: string;
  budget: number;
  start_date: string;
  end_date: string;
  status: string;
  objectives: any;
  target_metrics: any;
  actual_metrics: any;
  created_at: string;
  updated_at: string;
}

export const useBrandDeals = () => {
  const [deals, setDeals] = useState<BrandDeal[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useUser();

  const fetchBrandDeals = async () => {
    if (!currentUser?.user_id) {
      console.log('useBrandDeals: No currentUser.user_id available');
      return;
    }

    console.log('useBrandDeals: Fetching deals for user:', currentUser.user_id);

    setLoading(true);
    setError(null);
    try {
      // Use the optimized database function for brand deals
      const { data: optimizedDeals, error: dealsError } = await supabase
        .rpc('get_brand_deals_optimized', { brand_user_id: currentUser.user_id });

      if (dealsError) {
        console.error('useBrandDeals: Error fetching deals:', dealsError);
        setError('Failed to load deals');
        return;
      }

      // Get profile ID for brand_profile_id
      const { data: profileData } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', currentUser.user_id)
        .single();

      // Transform the optimized data to match the BrandDeal interface
      const transformedDeals = (optimizedDeals || []).map((deal: any) => ({
        id: deal.deal_id,
        title: deal.deal_title,
        description: deal.deal_description,
        deal_value: deal.deal_value,
        deal_type: deal.deal_type,
        status: deal.deal_status,
        priority: deal.deal_priority,
        deal_category: deal.deal_category,
        estimated_completion_date: deal.estimated_completion_date,
        actual_completion_date: deal.actual_completion_date,
        talent_profile_id: deal.talent_profile_id,
        brand_profile_id: profileData?.id || '',
        campaign_id: deal.campaign_id,
        created_at: deal.created_at,
        updated_at: deal.updated_at,
        talent: deal.talent_name ? {
          name: deal.talent_name,
          sport: deal.talent_sport,
          photo: deal.talent_photo,
        } : null,
        campaign: deal.campaign_name ? {
          name: deal.campaign_name,
          status: 'active',
        } : null,
        tasks: deal.tasks || [],
      }));

      console.log('useBrandDeals: Setting deals:', transformedDeals);
      setDeals(transformedDeals);

      // Fetch campaigns for brand users
      const { data: accountTypeData } = await supabase
        .from('profiles')
        .select('account_type')
        .eq('user_id', currentUser.user_id)
        .single();

      if (accountTypeData?.account_type === 'brand' && profileData?.id) {
        const { data: campaignsData, error: campaignsError } = await supabase
          .from('campaigns')
          .select('*')
          .eq('brand_profile_id', profileData.id)
          .order('created_at', { ascending: false });

        if (!campaignsError) {
          setCampaigns(campaignsData || []);
        }
      } else {
        setCampaigns([]);
      }

    } catch (err) {
      console.error('Error fetching brand deals:', err);
      setError('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only run when we have a current user and the user context is not loading
    if (currentUser?.user_id) {
      console.log('useBrandDeals: Effect triggered for user:', currentUser.user_id);
      fetchBrandDeals();
    }
  }, [currentUser?.user_id]);

  const createDeal = async (dealData: Partial<BrandDeal> & { talent_profile_id?: string }) => {
    if (!currentUser?.user_id) return;

    console.log('Creating deal with dealData:', dealData);

    const { data: userProfile } = await supabase
      .from('profiles')
      .select('id, account_type')
      .eq('user_id', currentUser.user_id)
      .maybeSingle();

    if (!userProfile) throw new Error('User profile not found');

    const insertData: any = {
      talent_profile_id: dealData.talent_profile_id || null,
      title: dealData.title || '',
      description: dealData.description,
      deal_value: dealData.deal_value || 0,
      deal_type: dealData.deal_type || 'sponsorship',
      status: dealData.status || 'pending',
      priority: dealData.priority || 'medium',
      estimated_completion_date: dealData.estimated_completion_date || null,
      campaign_id: dealData.campaign_id,
      eligibility_criteria: (dealData as any).eligibility_criteria || {},
      ...(userProfile.account_type === 'venture' 
        ? { 
            venture_profile_id: userProfile.id,
            deal_category: 'investment'
          }
        : { 
            brand_profile_id: userProfile.id 
          }
      ),
    };

    console.log('Preparing to insert deal:', insertData);

    // Prevent duplicate deals created within the last 3 minutes with same title/value/description
    const threeMinutesAgo = new Date(Date.now() - 3 * 60 * 1000).toISOString();
    
    let recentDealsQuery = supabase
      .from('deals')
      .select('*')
      .eq('title', insertData.title)
      .gte('created_at', threeMinutesAgo)
      .order('created_at', { ascending: false });

    // Filter by the correct profile ID based on account type
    if (userProfile.account_type === 'venture') {
      recentDealsQuery = recentDealsQuery.eq('venture_profile_id', userProfile.id);
    } else {
      recentDealsQuery = recentDealsQuery.eq('brand_profile_id', userProfile.id);
    }

    const { data: recentDeals, error: recentErr } = await recentDealsQuery;

    if (recentErr) {
      console.warn('Recent deals lookup failed:', recentErr);
    }

    const normalized = (s?: string) => (s || '').trim().toLowerCase();
    const possibleDuplicate = (recentDeals || []).find(d =>
      normalized(d.title) === normalized(insertData.title) &&
      Number(d.deal_value || 0) === Number(insertData.deal_value || 0) &&
      normalized(d.description) === normalized(insertData.description || '')
    );

    if (possibleDuplicate) {
      console.log('Duplicate deal detected, skipping insert. Returning existing deal:', possibleDuplicate.id);
      // Ensure state is up to date
      await fetchBrandDeals();
      return possibleDuplicate as any;
    }

    console.log('Inserting deal data:', insertData);

    const { data, error } = await supabase
      .from('deals')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('Deal creation error:', error);
      throw error;
    }
    
    console.log('Deal created successfully:', data);
    
    // Force refresh of deals data
    await fetchBrandDeals();
    return data;
  };

  const updateDeal = async (dealId: string, updates: Partial<BrandDeal>) => {
    const { error } = await supabase
      .from('deals')
      .update(updates)
      .eq('id', dealId);

    if (error) throw error;
    await fetchBrandDeals();
  };

  const createCampaign = async (campaignData: Partial<Campaign> & { name: string }) => {
    if (!currentUser?.user_id) return;

    const { data: brandProfile } = await supabase
      .from('profiles')
      .select('id')
      .eq('user_id', currentUser.user_id)
      .maybeSingle();

    if (!brandProfile) throw new Error('Brand profile not found');

    const { data, error } = await supabase
      .from('campaigns')
      .insert({
        name: campaignData.name,
        description: campaignData.description,
        budget: campaignData.budget || 0,
        start_date: campaignData.start_date,
        end_date: campaignData.end_date,
        status: campaignData.status || 'active',
        objectives: campaignData.objectives || [],
        target_metrics: campaignData.target_metrics || {},
        actual_metrics: campaignData.actual_metrics || {},
        brand_profile_id: brandProfile.id,
      })
      .select()
      .single();

    if (error) throw error;
    await fetchBrandDeals();
    return data;
  };

  const updateCampaign = async (campaignId: string, updates: Partial<Campaign>) => {
    const { error } = await supabase
      .from('campaigns')
      .update(updates)
      .eq('id', campaignId);

    if (error) throw error;
    await fetchBrandDeals();
  };

  // Calculate deal metrics
  const getMetrics = () => {
    const totalDeals = deals.length;
    const activeDeals = deals.filter(d => d.status === 'active').length;
    const completedDeals = deals.filter(d => d.status === 'completed').length;
    const totalInvestment = deals.reduce((sum, deal) => sum + (deal.deal_value || 0), 0);
    const avgDealValue = totalDeals > 0 ? totalInvestment / totalDeals : 0;

    return {
      totalDeals,
      activeDeals,
      completedDeals,
      totalInvestment,
      avgDealValue,
      totalCampaigns: campaigns.length,
      activeCampaigns: campaigns.filter(c => c.status === 'active').length,
    };
  };

  const createTask = async (dealId: string, taskData: Partial<DealTask>) => {
    const { error } = await supabase
      .from('deal_tasks')
      .insert({
        deal_id: dealId,
        title: taskData.title || '',
        description: taskData.description || '',
        status: taskData.status || 'pending',
        assigned_to: taskData.assigned_to || 'brand',
        due_date: taskData.due_date,
        progress_percentage: taskData.progress_percentage || 0,
        payment_amount: taskData.payment_amount || 0,
        payment_status: 'unpaid',
      });

    if (error) throw error;
    await fetchBrandDeals();
  };

  const updateTask = async (taskId: string, updates: Partial<DealTask>) => {
    const updateData: any = { ...updates };
    if (updates.status === 'completed' && !updates.completion_date) {
      updateData.completion_date = new Date().toISOString().split('T')[0];
    }

    const { error } = await supabase
      .from('deal_tasks')
      .update(updateData)
      .eq('id', taskId);

    if (error) throw error;
    await fetchBrandDeals();
  };

  const deleteTask = async (taskId: string) => {
    const { error } = await supabase
      .from('deal_tasks')
      .delete()
      .eq('id', taskId);

    if (error) throw error;
    await fetchBrandDeals();
  };

  const deleteDeal = async (dealId: string) => {
    const { error } = await supabase
      .from('deals')
      .delete()
      .eq('id', dealId);

    if (error) throw error;
    await fetchBrandDeals();
  };

  return {
    deals,
    campaigns,
    loading,
    error,
    fetchBrandDeals,
    createDeal,
    updateDeal,
    deleteDeal,
    createCampaign,
    updateCampaign,
    createTask,
    updateTask,
    deleteTask,
    metrics: getMetrics(),
  };
};